import { z } from 'zod';
/**
 * Nerdle — speed + arithmetic.
 *
 * Guess a hidden 8-character equation, e.g. `12+34=46`. Six rows, per-symbol
 * colour feedback with exactly Wordle's semantics: green = right symbol right
 * spot, amber = right symbol wrong spot, grey = absent.
 *
 * Both players get the same seeded equation.
 *
 * Scoring mirrors Wordle: on a SOLVE each guess beyond the first costs
 * NERDLE_EXTRA_GUESS_PENALTY_MS; on a FAILURE guesses are free and a flat
 * NERDLE_FAILED_SOLVE_PENALTY_MS applies instead. Charging both would punish
 * trying harder than not trying (plan doc §5.3).
 */
/** Every equation is exactly this many characters, `=` included. */
export const EQUATION_LENGTH = 8;
export const MAX_GUESSES = 6;
/** The keyboard. Digits, four operators, and equals. */
export const DIGITS = '0123456789';
export const OPERATORS = ['+', '-', '*', '/'];
export const EQUALS = '=';
/** Anchored: rejects unicode lookalikes, whitespace, and anything else. */
export const EQUATION_PATTERN = /^[0-9+\-*/=]{8}$/;
/**
 * Shown under the Nerdle header, and as the reason a guess bounced.
 *
 * The guess pool is every arithmetically-true 8-character equation, so a
 * rejection means the equation is false, malformed, or already tried.
 */
export const RULES_NOTE = 'SPEED + ARITHMETIC';
export const RULES_HINT = 'Must be a true equation — or you already tried it';
/* -------------------------------------------------------------------------- */
/* Client -> server events                                                     */
/* -------------------------------------------------------------------------- */
/**
 * A guess.
 *
 * The schema checks only the *shape* — 8 characters from the legal alphabet.
 * Whether the equation is TRUE is decided by the engine, which owns the rules.
 * Encoding arithmetic in a Zod schema would put game logic on the wire.
 */
export const NerdleGuessEvent = z.object({
    type: z.literal('guess'),
    equation: z.string().trim().regex(EQUATION_PATTERN, 'Must be 8 characters of 0-9 + - * / ='),
});
/**
 * Buy a hint: reveals one symbol and its exact position, at a flat time penalty.
 *
 * Position is picked from a seed-derived order, so both players who buy a hint
 * on the same puzzle get the same symbol. The match stays fair.
 */
export const NerdleHintEvent = z.object({
    type: z.literal('hint'),
});
export const NerdleEvent = z.discriminatedUnion('type', [NerdleGuessEvent, NerdleHintEvent]);
//# sourceMappingURL=nerdle.js.map